import PropTypes from "prop-types";

/**
 * RealisticHighbayLamp – High-fidelity 3D industrial LED highbay lamp component.
 * Features:
 * - Dynamic Light Brightness modulation responding directly to liveValue (0–100%)
 * - Downward light beam cone and lens diode flare with proportional lumen intensity
 * - Top suspension ring status indicator
 */
const RealisticHighbayLamp = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  // Dimming / brightness ratio (0.0 to 1.0) strictly based on configured percentage ratio
  const brightness = isRunning && !isOffline ? Math.min(1, Math.max(0.12, typeof ratio === "number" ? ratio : 1)) : 0;

  // Cone and flare opacities
  const coneOpacity = brightness * 0.75;
  const lensOpacity = brightness * 0.95;

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/highbay-lamp.png"
        alt="SCADA Realistic Highbay Lamp"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 377x311 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 377 311"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="lampConeGrad" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#fef08a" stopOpacity={coneOpacity} />
            <stop offset="40%" stopColor="#fef9c3" stopOpacity={coneOpacity * 0.5} />
            <stop offset="100%" stopColor="#fef08a" stopOpacity="0" />
          </linearGradient>
          <filter id="lampLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation={2 + brightness * 5} result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── Downward Light Cone Beam (Modulated Brightness) ── */}
        {brightness > 0 && (
          <polygon
            points="61,270 321,270 377,311 0,311"
            fill="url(#lampConeGrad)"
            style={{
              animation: "scada-light-cone 2.5s infinite ease-in-out",
              "--lamp-cone-op": coneOpacity,
            }}
          />
        )}

        {/* ── Illuminated LED Diode Matrix Flare (Lens cx: 191, cy: 260) ── */}
        {brightness > 0 && (
          <g filter="url(#lampLedGlow)">
            <ellipse
              cx="191"
              cy="260"
              rx={90 + brightness * 35}
              ry={18 + brightness * 8}
              fill="#fef08a"
              opacity={lensOpacity * 0.7}
            />
            <ellipse
              cx="191"
              cy="260"
              rx={60 + brightness * 20}
              ry={12 + brightness * 4}
              fill="#ffffff"
              opacity={lensOpacity}
            />
          </g>
        )}

        {/* ── Top Hanging Ring Status LED (3 Distinct States) ── */}
        <circle
          cx="191"
          cy="28"
          r="6"
          fill={dotColor}
          stroke="#ffffff"
          strokeWidth="1.8"
          filter={isRunning || (!isOffline && statusColor === "red") ? "url(#lampLedGlow)" : "none"}
          style={{
            animation: isRunning ? "scada-led-blink 1.8s infinite ease-in-out" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticHighbayLamp.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticHighbayLamp;
